import React, { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import NovaPoshtaAutoComplete from "../../components/NovaPoshtaAutoComplete";
import styles from "./AddressesSection.module.css";
import { FaTrash } from "react-icons/fa";
import { FiMapPin, FiPlusCircle } from "react-icons/fi";
import { useCustomer } from "../../context/CustomerContext";
import { validateNovaPostCheckoutDelivery } from "../../api/novaPoshtaDirectoryApi";
import {
  buildSavedNovaPoshtaAddressPayload,
  divisionFromSelection,
  formatNovaPostCityLabel,
  formatNovaPostDivisionLabel,
  settlementFromSelection,
} from "../../utils/novaPoshtaDelivery";

/* ========= ЛИШЕ ДЛЯ ВІДОБРАЖЕННЯ (UI) =========
   Перетворює "Відділення №332 (до 30 кг): вул..." ->
   'відділення "Нової пошти" №332 (до 30 кг): вул...'
   "Поштомат ..." -> 'поштомат "Нової пошти" ...'
   На бек нічого НЕ відправляємо через цей форматер.
================================================ */
const formatNpBranchForDisplay = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();

  // якщо вже містить бренд — просто робимо «відділення» з малої (косметика)
  if (/Нової\s+пошти/i.test(s)) {
    return s.replace(/\bВідділення\b/u, "відділення");
  }

  // поштомат
  const mPost = s.match(
    /^\s*(Поштомат|поштомат)\s*(?:№|No\.?|N°)?\s*(\d+)?\s*(\([^)]*\))?\s*(?::\s*(.*))?$/u
  );
  if (mPost) {
    const num = mPost[2] ? ` №${mPost[2]}` : "";
    const extra = mPost[3] ? ` ${mPost[3]}` : "";
    const tail = mPost[4] ? `: ${mPost[4].trim()}` : "";
    return `поштомат "Нової пошти"${num}${extra}${tail}`.trim();
  }

  // відділення
  const mDep = s.match(
    /^\s*(Відділення|відділення)\s*(?:№|No\.?|N°)?\s*(\d+)\s*(\([^)]*\))?\s*(?::\s*(.*))?$/u
  );
  if (mDep) {
    const num = mDep[2];
    const extra = mDep[3] ? ` ${mDep[3]}` : "";
    const tail = mDep[4] ? `: ${mDep[4].trim()}` : "";
    return `відділення "Нової пошти" №${num}${extra}${tail}`;
  }

  // якщо слово є, але структура інша — міняємо тільки слово
  if (/\b(Відділення|відділення)\b/u.test(s)) {
    return s.replace(
      /\b(Відділення|відділення)\b/u,
      'відділення "Нової пошти"'
    );
  }

  return s;
};

const renderNpDisplayAddress = (addr) => {
  const delivery = addr?.novaPoshtaDelivery;
  const cityPart = formatNovaPostCityLabel(delivery?.city, addr?.city);
  const savedDivision = delivery?.division;
  const branchRaw =
    savedDivision?.name ??
    addr?.branchNumber ??
    addr?.branch ??
    addr?.branchText ??
    "";
  const branch = savedDivision
    ? formatNovaPostDivisionLabel(savedDivision, branchRaw)
    : formatNpBranchForDisplay(branchRaw);
  return [cityPart, branch].filter(Boolean).join(", ");
};
/* ============================================== */

const AddressesSection = () => {
  const [addresses, setAddresses] = useState([]);
  const [city, setCity] = useState("");
  const [branchNumber, setBranchNumber] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addError, setAddError] = useState("");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 700 : false
  );

  const { refreshCustomer } = useCustomer();

  useEffect(() => {
    const handleResize = () =>
      setIsMobile(typeof window !== "undefined" && window.innerWidth < 700);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/customers/me/addresses");
      const sorted = [...res.data].sort(
        (a, b) => (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0)
      );
      setAddresses(sorted);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  const handleAddAddress = async (e) => {
    e.preventDefault();
    setAddError("");

    if (!city || !branchNumber || !selectedCity?.id || !selectedDivision?.divisionId) {
      setAddError("Оберіть місто та відділення зі списку Nova Post");
      return;
    }

    if (
      selectedDivision.settlementId &&
      Number(selectedDivision.settlementId) !== Number(selectedCity.id)
    ) {
      setAddError("Вибране відділення не належить вибраному місту");
      return;
    }

    try {
      const addressPayload = buildSavedNovaPoshtaAddressPayload({
        citySelection: selectedCity,
        divisionSelection: selectedDivision,
        method: "branch",
      });
      await validateNovaPostCheckoutDelivery({
        deliveryMethod: "branch",
        novaPoshtaDelivery: addressPayload.novaPoshtaDelivery,
      });
      await axiosInstance.post("/customers/me/addresses", addressPayload);

      setCity("");
      setBranchNumber("");
      setSelectedCity(null);
      setSelectedDivision(null);

      await refreshCustomer();
      fetchAddresses();
    } catch (err) {
      setAddError(
        err?.response?.data?.message || "Не вдалося додати адресу"
      );
    }
  };

  const handleSetMain = async (id) => {
    await axiosInstance.put(`/customers/me/addresses/${id}/set-main`);
    await refreshCustomer();
    fetchAddresses();
  };

  const handleDelete = async (id) => {
    await axiosInstance.delete(`/customers/me/addresses/${id}`);
    await refreshCustomer();
    fetchAddresses();
  };

  return (
    <div className={styles.ordersRoot}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>
          <FiMapPin />
        </div>
        <div>
          <span>Доставка</span>
          <h1>Адреси доставки</h1>
          <p>Збережені відділення й адреси, які можна швидко використати під час оформлення замовлення.</p>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Завантаження…</div>
      ) : addresses.length === 0 ? (
        <div className={styles.empty}>Збережених адрес ще немає.</div>
      ) : (
        <div
          className={
            isMobile ? styles.addressesListMobile : styles.addressesList
          }
        >
          {addresses.map((addr) => (
            <div
              key={addr._id}
              className={[
                isMobile ? styles.addressCardMobile : styles.addressCard,
                addr.isMain ? styles.mainCard : "",
                styles.addressCardWithTrash,
              ].join(" ")}
            >
              <div className={styles.cardTopRow}>
                <div className={styles.cardStatusWrap}>
                  {addr.isMain ? (
                    <span className={styles.mainBadge}>Основна</span>
                  ) : (
                    <span className={styles.secondaryBadge}>Додаткова</span>
                  )}
                  {!addr.isMain && (
                    <button
                      type="button"
                      className={styles.setMainBtn}
                      onClick={() => handleSetMain(addr._id)}
                    >
                      Зробити основною
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.trashBtn}
                  onClick={() => handleDelete(addr._id)}
                  title="Видалити адресу"
                  aria-label="Видалити адресу"
                >
                  <FaTrash />
                </button>
              </div>

              <div>
                {/* ✅ ТІЛЬКИ ДЛЯ UI: показуємо красивий формат */}
                <span className={addr.isMain ? styles.main : ""}>
                  {renderNpDisplayAddress(addr)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ДОДАВАННЯ НОВОЇ АДРЕСИ */}
      <form
        className={styles.addFormBlock}
        onSubmit={handleAddAddress}
        autoComplete="off"
      >
        <div className={styles.addFormHeader}>
          <FiPlusCircle />
          <div>
            <h2>Додати адресу</h2>
            <p>Оберіть місто та відділення Нової пошти.</p>
          </div>
        </div>
        <div className={styles.addFormInputsRow}>
          <div className={styles.inputCol}>
            <span className={styles.inputLabel}>Місто</span>
            <NovaPoshtaAutoComplete
              mode="settlement"
              value={city}
              onChange={(value) => {
                setCity(value);
                setSelectedCity(null);
                setBranchNumber("");
                setSelectedDivision(null);
              }}
              onSelect={(selection) => {
                if (!selection) return;
                setSelectedCity(settlementFromSelection(selection));
                setCity(selection.displayName);
              }}
              inputProps={{
                className: styles.inputModern,
                placeholder: "Введіть місто",
              }}
            />
          </div>
          <div className={styles.inputCol}>
            <span className={styles.inputLabel}>Відділення Нової Пошти</span>
            <NovaPoshtaAutoComplete
              mode="branch"
              value={branchNumber}
              settlement={selectedCity}
              onChange={(value) => {
                setBranchNumber(value);
                setSelectedDivision(null);
              }}
              onSelect={(selection) => {
                if (!selection) return;
                const division = divisionFromSelection(selection);
                setSelectedDivision(division);
                setBranchNumber(
                  buildSavedNovaPoshtaAddressPayload({
                    citySelection: selectedCity,
                    divisionSelection: division,
                    method: "branch",
                  }).branchNumber
                );
              }}
              inputProps={{
                className: styles.inputModern,
                placeholder: "Оберіть відділення",
              }}
            />
          </div>
        </div>
        <div className={styles.addFormBtnWrap}>
          <button type="submit" className={styles.addBtnModern}>
            Додати нову адресу
          </button>
        </div>
        {addError && <div className={styles.errorMsg}>{addError}</div>}
      </form>
    </div>
  );
};

export default AddressesSection;
